<template lang="pug">
.message-confirm-modal
  b-modal(ref="modal" size="lg" no-close-on-backdrop no-close-on-esc @ok="submit")
    div(slot="modal-title")
      span.message-confirm-modal-title(v-if="validated == true && isApprovalMode == true") 以下の内容でメッセージの承認依頼を行います。よろしいですか？
      span.message-confirm-modal-title(v-else-if="validated == true") 以下の内容でメッセージを送信します。よろしいですか？
      span.message-confirm-modal-title(v-else) メッセージプレビュー

    .message-confirm-modal-body
      .header
        .address.d-flex.flex-row
          span(:class="_addressCssClass") 宛先： {{ _receivers }}
          .btn.address-toggle-btn
            .toggle-address.open(v-show="!isAddressOpen" @click="onAddressBadge()")
            .toggle-address.close(v-show="isAddressOpen" @click="onAddressBadge()")
      .body.d-flex.flex-column
        .message-text
          SendMessagePreview(v-model="functionContents" :replyLimit="getReplyLimit()")

    .message-confirm-modal-footer(slot="modal-footer" slot-scope="{ ok, cancel }")
      .d-flex.flex-column.flex-fill
        .d-flex.flex-fill.mb-2(v-if="isSchedulingMessage")
          .register-body.d-flex.flex-column
            FormDatetimePicker(v-model="scheduledTime" :date.sync="scheduledTimeDate" :time.sync="scheduledTimeTime"
              key-name="送信日時" name="scheduled-time" placeholder="送信日時"
              :validation-rules="{ required: true }" required)
        .d-flex.flex-fill.mb-2(v-if="showCheckbox == true")
          .d-flex.flex-column
            span.warning-message {{ warningMessage }}
            b-form-checkbox(v-model="checked") 確認しました
        .d-flex
          .file(v-if="0 < files.length")
            span.title 添付ファイル（{{ files.length }}）
            button.btn.preview-btn(@click="onPreview()") プレビュー
          .buttons
            ValidationAlert(v-model="showValidationAlert")
            .m-0
              button.btn.modal-ok-btn(v-if="validated == true" :class="{ 'disable': !checked }" :disabled="!checked"
                type="button" @click="ok()") {{ isApprovalMode ? '依頼者選択' : 'OK' }}
              button.btn.modal-cancel-btn(v-if="validated == true" type="button" @click="cancel()") キャンセル
              button.btn.modal-cancel-btn(v-else type="button" @click="cancel()") 閉じる
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { ReceiverDetail } from "@/commons/models/SendLineMessage";
import { SendMessageFunction } from "@/commons/models/Message";

import SendMessagePreview from "@/pc/basics/SendMessagePreview.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";

/**
 * メッセージ送信確認モーダル
 */
@Component({
  components: {
    SendMessagePreview,
    FormDatetimePicker,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessageModal extends Vue {
  @Prop({
    default: () => {
      return [];
    }
  })
  files!: [];

  @Prop({
    default: false
  })
  isSchedulingMessage!: boolean;

  /** 注意メッセージ文言 */
  @Prop({
    default: "家族全員へのメッセージで良いでしょうか？"
  })
  warningMessage!: string;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 表示対象のメッセージ */
  receivers: Array<ReceiverDetail> = [];

  /** 表示対象のメッセージ */
  functionContents: any = {};

  /** 表示する情報がバリデーション済みか */
  validated: boolean = false;
  /** 家族全員へのメッセージで良いでしょうか？のチェックボックスを表示するか否か */
  showCheckbox: boolean = false;
  /** 家族全員へのメッセージで良いでしょうか？のチェックボックスのチェック */
  checked: boolean = false;

  /** 送信日時 */
  scheduledTime: number | null = null;
  /** 送信日時(日) */
  scheduledTimeDate: string = "";
  /** 送信日時(時) */
  scheduledTimeTime: string = "";

  /** 宛先CSSクラス */
  isAddressOpen: boolean = false;

  /** 承認依頼モードか否か */
  isApprovalMode: boolean = false;

  /**
   * ### モーダル表示
   */
  public show(receivers: Array<ReceiverDetail>, functionContents: SendMessageFunction, validated: boolean, showCheckbox: boolean, isApprovalMode: boolean = false): void {
    this.receivers = receivers;
    this.functionContents = functionContents;
    this.validated = validated;
    this.showCheckbox = showCheckbox;
    this.checked = !showCheckbox;
    this.showValidationAlert = false;
    this.isApprovalMode = isApprovalMode;
    this.scheduledTime = null;
    this.scheduledTimeDate = "";
    this.scheduledTimeTime = "";
    this.$validator.errors.clear();

    const modal: any = this.$refs.modal;
    if (modal) {
      modal.show();
    }

    return;
  }

  public hide(): void {
    const modal: any = this.$refs.modal;
    if (modal) {
      modal.hide();
    }

    return;
  }

  /**
   * ### 外部（APIエラー等）からエラーメッセージを設定する
   */
  public setError(msg: string): void {
    this.$validator.errors.remove("scheduled-time");
    this.$validator.errors.add({ field: "scheduled-time", msg: msg });
    this.showValidationAlert = true;
    return;
  }

  /**
   * ### メッセージ返信可能期限を返す
   */
  public getReplyLimit(): number {
    return userStore.facility.messageSettings.replyLimit;
  }

  get _receivers(): string {
    if (this.receivers == undefined) {
      return "";
    }

    let address: string = "";

    for (const receiver of this.receivers) {
      // 詳細タブの宛先は家族の場合は（部屋名・利用者名）をつけるように仕様変更になったのでここで判定する
      let receiverName: string = receiver.room && receiver.residentName ? `${receiver.name}（${receiver.room}・${receiver.residentName}）` : receiver.name;

      if (address == "") {
        address += receiverName;
      } else {
        address += "｜" + receiverName;
      }
    }

    return address;
  }

  get _addressCssClass(): string {
    if (this.isAddressOpen) {
      return "address-open";
    } else {
      return "address-close";
    }
  }

  /**
   * ### 宛先のオープン/クローズ切り替え
   */
  public onAddressBadge(): void {
    this.isAddressOpen = !this.isAddressOpen;

    return;
  }

  /**
   * ### プレビューボタン押下イベント
   */
  public onPreview(): void {
    this.$emit("preview");
    return;
  }

  /**
   * ### 送信する／依頼者選択ボタン押下イベント
   */
  public async submit(modalEvent: any): Promise<void> {
    modalEvent.preventDefault();

    const validationResult: boolean = await this.$validator.validateAll();
    const scheduledTimeValidationResult: boolean = this.scheduledTimeValidate(this.scheduledTime);

    if (validationResult == false || scheduledTimeValidationResult == false) {
      this.showValidationAlert = true;
    } else {
      this.showValidationAlert = false;
      if (this.isApprovalMode) {
        this.hide();
      }
      this.$emit("submit", this.scheduledTime, this.isApprovalMode);
    }
    return;
  }

  private scheduledTimeValidate(scheduledTime: number | null): boolean {
    this.$validator.errors.remove("scheduled-time");

    if (scheduledTime == null) {
      return true;
    } else {
      // 現在日時より10分後でなければエラー
      const currentTime: Date = new Date();
      const scheduledTimeDate: Date = this.$dateUtil.convertNumberToDate(scheduledTime);
      const limitTime: Date = new Date(currentTime.getTime() + 10 * 60 * 1000);
      if (scheduledTimeDate < limitTime) {
        this.$validator.errors.add({ field: "scheduled-time", msg: "送信日時は現在日時より10分以上後の日時を設定してください" });
        return false;
      } else {
        return true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.register-body {
  ::v-deep .register-group {
    margin: 0 !important;
  }
}
</style>
